import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable } from 'rxjs';

import * as io from 'socket.io-client';

import { Health } from 'src/app/stores/global/interface';
import {
  AddPickupErrorResponse,
  AddPickupResponse,
  FinishPickupErrorResponse,
  FinishPickupRequest,
  FinishPickupResponse,
  GetPickupStateResponse,
  OpenDoorErrorResponse,
  OpenDoorResponse,
  StartPickupErrorResponse,
  StartPickupResponse,
} from 'src/app/stores/remote-pickup/interfaces';
import { RUN_MODE_TOKEN, RunMode } from 'src/environments/runtime';

import { retryStrategy } from '../../utils';
import { FRODO_API_WS_PATH, GATEWAY_SERVICES } from '../../utils/constants';
import { TSettings } from '../appconfig/settings.model';
import { EndpointService } from '../endpoint/endpoint.service';

export const FRODO_SOCKET_EVENTS = {
  SETTINGS_UPDATED: 'settings_updated',
  DOOR_CLOSED: 'door_closed',
  SPACE_PRESENCE: 'space_presence',
} as const;

@Injectable({
  providedIn: 'root',
})
export class FrodoService {
  private socket_: io.socket;
  private frodoApiUrl: string;
  private readonly endpoints = {
    getSettings: '/settings',
    addPickup: '/pickups/add',
    finishPickup: '/pickups/finish',
    startPickup: '/pickups/start',
    getPickupState: '/pickups/state',
    openSpaceDoor: '/spaces/open_door',
    health: '/health',
  };

  constructor(
    private httpClient: HttpClient,
    private endpointService: EndpointService,
    @Optional() @Inject(RUN_MODE_TOKEN) private runMode: RunMode | null
  ) {
    this.frodoApiUrl = this.endpointService.getFrodoAPIUrl();

    let wsPath: string;
    let wsQuery: Record<string, string> | undefined;
    if (this.runMode === 'cloud' && this.endpointService.nodeId) {
      wsPath = `/gateway/${GATEWAY_SERVICES.FRODO_API}/socket.io`;
      wsQuery = { deviceId: this.endpointService.nodeId };
    } else if (this.endpointService.nodeId) {
      wsPath = String(FRODO_API_WS_PATH).replace('{0}', this.endpointService.nodeId);
    } else {
      wsPath = FRODO_API_WS_PATH;
    }

    const socketUrl = new URL(this.frodoApiUrl).origin;
    this.socket_ = io.connect(socketUrl, {
      path: wsPath,
      query: wsQuery,
      transports: ['websocket'],
      reconnectionDelay: 2000,
      reconnectionDelayMax: 30000,
      randomizationFactor: 0.5,
    });
  }

  get socket() {
    return this.socket_;
  }

  listen(eventName: string, callbackFn: (...args: any[]) => unknown) {
    this.socket_.off(eventName);
    this.socket_.on(eventName, callbackFn);
  }

  addPickup(code: string): Observable<HttpResponse<AddPickupResponse | AddPickupErrorResponse>> {
    return this.httpClient.post<AddPickupResponse | AddPickupErrorResponse>(
      `${this.frodoApiUrl}${this.endpoints.addPickup}`,
      { code },
      { observe: 'response' }
    );
  }

  finishPickup(
    request: FinishPickupRequest
  ): Observable<FinishPickupResponse | FinishPickupErrorResponse> {
    return this.httpClient.post<FinishPickupResponse | FinishPickupErrorResponse>(
      `${this.frodoApiUrl}${this.endpoints.finishPickup}`,
      request
    );
  }

  getPickupState(scheduleId: string): Observable<GetPickupStateResponse> {
    return this.httpClient.get<GetPickupStateResponse>(
      `${this.frodoApiUrl}${this.endpoints.getPickupState}/${scheduleId}`
    );
  }

  startPickup(scheduleId: string): Observable<StartPickupResponse | StartPickupErrorResponse> {
    return this.httpClient.post<StartPickupResponse | StartPickupErrorResponse>(
      `${this.frodoApiUrl}${this.endpoints.startPickup}`,
      { scheduleId }
    );
  }

  getSettings(): Observable<Partial<TSettings>> {
    return this.httpClient
      .get<Partial<TSettings>>(`${this.frodoApiUrl}${this.endpoints.getSettings}`)
      .pipe(retryStrategy());
  }

  openSpaceDoor(spaceId: string): Observable<OpenDoorResponse | OpenDoorErrorResponse> {
    return this.httpClient.post<OpenDoorResponse | OpenDoorErrorResponse>(
      `${this.frodoApiUrl}${this.endpoints.openSpaceDoor}`,
      { spaceId }
    );
  }

  getHealth(): Observable<Health> {
    return this.httpClient.get<Health>(`${this.frodoApiUrl}${this.endpoints.health}`);
  }
}
